import { useT } from "@/i18n/I18nContext";
import { CheckCircle2, Circle, Scale } from "lucide-react";

export const CASE_STAGES = [
  "intake",
  "investigation",
  "treatment",
  "demand_sent",
  "negotiation",
  "settlement",
  "closed",
];

/**
 * Horizontal 7-stage pipeline. Highlights the current stage and shows progress.
 * - `current`: one of CASE_STAGES (defaults to "intake")
 * - `onSelect(stage)`: optional click handler (attorney use; omit for read-only)
 * - `compact`: tighter mobile/sidebar layout
 */
export default function CaseStagePipeline({ current = "intake", onSelect, compact = false }) {
  const t = useT();
  const currentIdx = Math.max(0, CASE_STAGES.indexOf(current));
  return (
    <ol
      className={`grid gap-3 ${compact ? "sm:grid-cols-7" : "sm:grid-cols-4 lg:grid-cols-7"}`}
      data-testid="case-stage-pipeline"
    >
      {CASE_STAGES.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        const Icon = done ? CheckCircle2 : active ? Scale : Circle;
        const dotClass = active
          ? "bg-[#E85D04] border-[#E85D04] text-white shadow-md"
          : done
            ? "bg-[#11322A] border-[#11322A] text-white"
            : "bg-white border-[#E0E4E2] text-[#5C6B68]";
        const labelClass = active
          ? "text-[#E85D04] font-black"
          : done
            ? "text-[#11322A]"
            : "text-[#5C6B68]";
        const Wrapper = onSelect ? "button" : "div";
        return (
          <Wrapper
            key={s}
            type={onSelect ? "button" : undefined}
            onClick={onSelect ? () => onSelect(s) : undefined}
            className={`group flex items-start gap-2 rounded-lg p-2 text-left ${onSelect ? "transition hover:bg-[#FDFBF7]" : ""}`}
            data-testid={`stage-${s}${active ? "-current" : ""}`}
          >
            <div
              className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full border ${dotClass}`}
            >
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className={`text-[11px] font-bold uppercase tracking-[0.12em] ${labelClass}`}>
                {t(`stage.${s}`)}
              </div>
              {active && (
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#E85D04]">
                  {t("stage.current")}
                </div>
              )}
            </div>
          </Wrapper>
        );
      })}
    </ol>
  );
}
